<template>
  <FilterChip
    :is-text="showsText"
    :label="showsText ? label : null"
    :icon="icon"
    :tooltip="showsText ? null : label"
    :active="active"
    has-popup
    date-variant
    :open="open"
    :pop-title="label"
    @toggle="open = !open"
    @close="open = false"
    @clear="onClear"
  >
    <DatePicker
      variant="full"
      range
      :propDates="propDates"
      :notice="notice"
      :datePickerValues="periods"
      @on-save-dates="onSave"
      @on-clear-dates="onClear"
      @on-close-menu="open = false"
    />
  </FilterChip>
</template>

<script>
import FilterChip from '@/components/filters/FilterChip';
import DatePicker from '@/components/form/DatePicker';
import { formatDateToString } from '@/util/date-util';

/**
 * Generic date-range filter chip: FilterChip (date variant) + the DS DatePicker in its popover.
 * Presentational — `:label :icon :value(date range) :notice :periods` in, `change`(sorted dates
 * array)/`clear` out. The DatePicker owns its own save/clear controls, so the chip has no footer
 * clear. Pre-formats dates to strings (VDatePicker calls .split() and throws on raw moments).
 */
export default {
  name: 'FilterDateChip',
  components: { FilterChip, DatePicker },
  inject: { labelled: { from: 'filterChipsLabelled', default: false } },
  props: {
    label: { type: String, required: true },
    icon: { type: String, default: 'calendar' },
    // Show the dimension name on the chip face (used in the compact stacked filters dialog).
    isText: { type: Boolean, default: false },
    value: { type: Array, default: () => [] }, // date range
    notice: { type: String, default: null },
    periods: { type: Array, default: () => [] },
  },
  data() {
    return { open: false };
  },
  computed: {
    // In a phone's list of filters or line of filters in use (FilterChipsContext), it names itself.
    showsText() {
      return this.isText || this.labelled;
    },
    active() {
      return (this.value || []).length > 0;
    },
    propDates() {
      return (this.value || []).filter(Boolean).map((d) => formatDateToString(d));
    },
  },
  methods: {
    onSave(dates) {
      // Dates are strings (e.g. "2026-07-10"); compare lexically — ISO dates sort chronologically,
      // and numeric subtraction (a - b) on strings would be NaN (a no-op sort).
      const sorted = [...dates].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
      this.$emit('change', sorted);
      this.open = false;
    },
    onClear() {
      this.$emit('change', []);
      this.open = false;
    },
  },
};
</script>

<style lang="scss">
// The date popover embeds the full DatePicker (presets + calendar), which sizes to its own
// content rather than the compact chip-popover box.
.filter-chip__pop--date {
  padding: 0;
  min-width: 0;
  max-height: none;
  overflow: visible;

  .filter-chip__pop-title {
    margin: 10px 12px 8px;
  }

  .date-picker {
    border-top: 1px solid $color-border;
    border-radius: 0 0 $border-radius-root $border-radius-root;
    overflow: hidden;
  }
}
</style>
